import React, {useEffect, useState} from "react";
import {Col, Select} from "antd";
import _ from "lodash";
import {ShiftSuggestionFilterPropTypes} from "./ShiftSuggestionComponentPropTypes";
import {
    CityFilter,
    CompanyFilter,
    DatePickerWithPeriod,
    SearchText,
    SubCompanyFilter
} from "../../_defaultComponents/FilterComponents";
import ShiftSuggestionStatusFilter from "../../_defaultComponents/FilterComponents/ShiftSuggestionStatusFilter";
import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import {useTranslation} from "react-i18next";

const ShiftSuggestionFilters = ({
    setCurrentPage,
    filters,
    setFilters
}: ShiftSuggestionFilterPropTypes) => {
    const [city, setCity] = useState(filters.city);
    const [company, setCompany] = useState(filters.company);
    const [subcompany, setSubcompany] = useState(filters.subcompany);
    const [period, setPeriod] = useState(filters.period);
    const [startDate, setStartDate] = useState(filters.startDate);
    const [endDate, setEndDate] = useState(filters.endDate);
    const [searchText, setSearchText] = useState(filters.searchText);
    const [status, setStatus] = useState(filters.status);
    const [upperGroups, setUpperGroups] = useState(filters.upperGroups);
    const stores = useStore();
    const {t} = useTranslation();

    useEffect(() => {
        if (city !== "") {
            setCompany(undefined);
            setSubcompany(undefined);
            setUpperGroups([]);
        }
    }, [city]);
    useEffect(() => {
        setSubcompany(undefined);
    }, [company]);

    useEffect(() => {
        setCurrentPage(1);
    }, [city, company, subcompany, period, startDate, endDate, searchText, status, upperGroups]);

    useEffect(() => {
        const newFilters = {
            city,
            company,
            subcompany,
            period,
            startDate,
            endDate,
            searchText,
            status,
            upperGroups
        };
        if (JSON.stringify(filters) !== JSON.stringify(newFilters)) {
            setFilters(newFilters);
        }
    }, [city, company, subcompany, period, startDate, endDate, searchText, status, upperGroups]);

    const [
        upperGroupsByCompanyAndCityLoading,
        upperGroupsByCompanyAndCity,
        setUpperGroupsByCompanyAndCity,
        setUpperGroupsByCompanyAndCityLoading
    ] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.regionStore.getUpperGroupsByCompanyIdAndCity.bind(null, {
            companyId: company,
            city: city
        }),
        dependencies: [city, company],
        initialCall: true,
        condition: city != undefined
    });

    return (
        <React.Fragment>
            <Col xs={24} sm={10} md={6} lg={6} xl={4}>
                <ShiftSuggestionStatusFilter value={status} onChange={setStatus} />
            </Col>
            <Col xs={20} sm={10} md={6} lg={6} xl={4}>
                <CityFilter value={city} onChange={setCity} />
            </Col>
            <Col xs={20} sm={10} md={6} lg={6} xl={4}>
                <h5>{t("fields.upperGroup", {ns: "common"})}</h5>
                <Select
                    style={{width: "100%"}}
                    value={filters.upperGroup}
                    onChange={value => {
                        setUpperGroups(value);
                    }}
                    mode="multiple"
                    loading={upperGroupsByCompanyAndCityLoading}
                    disabled={!city || upperGroupsByCompanyAndCityLoading}
                    notFoundContent={t("filters.dataNotFound", {ns: "common"})}
                    placeholder={t("filters.addFilter", {ns: "common"})}
                    optionFilterProp="children"
                    data-testid="upperGroupSelect"
                    filterOption={(input, option) =>
                        option.children
                            .toString()
                            .toLocaleLowerCase("tr-TR")
                            .indexOf(input.toLocaleLowerCase("tr-TR")) >= 0
                    }
                    filterSort={(optionA, optionB) =>
                        optionA.children
                            .toString()
                            .toLocaleLowerCase("tr-TR")
                            .localeCompare(optionB.children.toString().toLocaleLowerCase("tr-TR"))
                    }
                >
                    <Select.Option value="all">{t("filters.all", {ns: "common"})}</Select.Option>
                    {upperGroupsByCompanyAndCity.map(upperGroup => (
                        <Select.Option
                            key={upperGroup.group}
                            value={upperGroup.group}
                            data-testid="upperGroupSelect_option"
                        >
                            {upperGroup.group}
                        </Select.Option>
                    ))}
                </Select>
            </Col>
            <Col xs={20} sm={10} md={8} lg={6} xl={4}>
                <CompanyFilter value={company} onChange={setCompany} />
            </Col>

            <Col xs={20} sm={10} md={8} lg={6} xl={4}>
                <SubCompanyFilter
                    value={subcompany}
                    onChange={setSubcompany}
                    company={company}
                    disabled={!(Boolean(company) && !["pool", "all"].includes(company))}
                />{" "}
            </Col>

            <Col xs={20} sm={10} md={8} lg={6} xl={4}>
                <SearchText value={searchText} onChange={setSearchText} />
            </Col>
            <Col xs={48} sm={40} md={32} lg={20} xl={16}>
                <DatePickerWithPeriod
                    period={period}
                    setPeriod={setPeriod}
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                />
            </Col>
        </React.Fragment>
    );
};

export default ShiftSuggestionFilters;
